/**
 * WatchAdButton Component
 * Button to watch rewarded ads and earn credits
 * 
 * Features:
 * - Shows remaining daily rewards
 * - Different CTAs based on credit level
 * - Handles ad loading and reward claiming
 * - Turkish language UI
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useRewardedAds from '../hooks/useRewardedAds';
import { 
  trackRewardedAdStarted, 
  trackRewardedAdCompleted, 
  trackRewardedCreditGranted,
  trackRewardedAdFailed,
  trackRewardedDailyLimitReached 
} from '../services/analytics';

const WatchAdButton = ({ 
  userId, 
  currentCredits = 0, 
  onCreditEarned,
  variant = 'default', // 'default', 'prominent', 'minimal'
  showOnlyWhenLow = false, // Only show when credits < 5
  className = ''
}) => {
  const {
    isLoading,
    isAdReady,
    error,
    rewardStatus,
    adConfig,
    isNative,
    showRewardedAd,
    loadRewardedAd,
    refreshStatus,
    clearError
  } = useRewardedAds(userId);

  const [showResult, setShowResult] = useState(null);
  const [isWatching, setIsWatching] = useState(false);

  // Don't render if feature is disabled
  if (!rewardStatus.enabled) {
    return null;
  }

  // If showOnlyWhenLow is true, only show when credits < 5
  if (showOnlyWhenLow && currentCredits >= 5) {
    return null;
  }

  // Handle watching ad
  const handleWatchAd = async () => {
    if (!rewardStatus.can_watch_ad || isWatching) {
      if (!rewardStatus.can_watch_ad) {
        trackRewardedDailyLimitReached(userId);
      }
      return;
    }

    setIsWatching(true);
    clearError();
    
    // Track ad started
    trackRewardedAdStarted(adConfig?.ad_unit_id);

    const result = await showRewardedAd();

    setIsWatching(false);

    if (result.success) {
      // Track successful completion and credit grant
      trackRewardedAdCompleted(adConfig?.ad_unit_id, result.credits_granted);
      trackRewardedCreditGranted(result.credits_granted, result.total_credits);
      
      setShowResult({
        type: 'success',
        message: result.message || 'Tebrikler! 1 kredi kazandınız!',
        credits: result.total_credits,
        remaining: result.daily_rewards_remaining
      });

      // Notify parent component
      if (onCreditEarned) {
        onCreditEarned(result.credits_granted, result.total_credits);
      }

      // Load next ad
      loadRewardedAd();
    } else {
      // Track failure
      trackRewardedAdFailed(adConfig?.ad_unit_id, result.error);
      
      setShowResult({
        type: 'error',
        message: result.error || 'Bir hata oluştu'
      });
    }

    // Auto-hide result after 3 seconds
    setTimeout(() => setShowResult(null), 3000);
  };

  // Different CTA based on credit level
  const getCTA = () => {
    if (!rewardStatus.can_watch_ad) {
      return {
        text: 'Günlük limit doldu',
        subtext: 'Yarın tekrar deneyin',
        disabled: true
      };
    }

    if (currentCredits === 0) {
      return {
        text: '📺 Reklam İzle, Kredi Kazan!',
        subtext: `Krediniz bitti! ${rewardStatus.rewards_remaining} hakkınız kaldı`,
        disabled: false,
        urgent: true
      };
    }

    if (currentCredits < 5) {
      return {
        text: '📺 Ücretsiz Kredi Kazan',
        subtext: `${rewardStatus.rewards_remaining} reklam hakkı • 1 reklam = 1 kredi`,
        disabled: false
      };
    }

    return {
      text: '📺 Reklam İzle',
      subtext: `${rewardStatus.rewards_remaining}/${rewardStatus.daily_limit || 5} hak`,
      disabled: false
    };
  };

  const cta = getCTA();

  // Minimal variant (small badge-like)
  if (variant === 'minimal') {
    return (
      <button
        onClick={handleWatchAd}
        disabled={cta.disabled || isWatching}
        className={`watch-ad-btn-minimal ${className}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 12px',
          background: cta.disabled ? 'rgba(255,255,255,0.1)' : 'rgba(74,222,128,0.15)',
          border: `1px solid ${cta.disabled ? 'rgba(255,255,255,0.1)' : 'rgba(74,222,128,0.3)'}`,
          borderRadius: '20px',
          color: cta.disabled ? 'rgba(255,255,255,0.4)' : '#4ade80',
          fontSize: '12px',
          fontWeight: '500',
          cursor: cta.disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s'
        }}
      >
        <span>📺</span>
        <span>+1 💎</span>
        {rewardStatus.rewards_remaining > 0 && (
          <span style={{ opacity: 0.7 }}>({rewardStatus.rewards_remaining})</span>
        )}
      </button>
    );
  }

  // Prominent variant (for empty credits state)
  if (variant === 'prominent' || cta.urgent) {
    return (
      <>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className={`watch-ad-container-prominent ${className}`}
          style={{
            background: 'linear-gradient(135deg, rgba(74,222,128,0.15), rgba(74,222,128,0.05))',
            border: '1px solid rgba(74,222,128,0.3)',
            borderRadius: '16px',
            padding: '20px',
            textAlign: 'center',
            marginTop: '16px'
          }}
        >
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>📺</div>
          
          <h3 style={{ 
            color: '#4ade80', 
            fontSize: '18px', 
            fontWeight: '600',
            margin: '0 0 4px'
          }}>
            {cta.text}
          </h3>
          
          <p style={{ 
            color: 'rgba(255,255,255,0.6)', 
            fontSize: '14px',
            margin: '0 0 16px'
          }}>
            {cta.subtext}
          </p>

          <button
            onClick={handleWatchAd}
            disabled={cta.disabled || isWatching}
            style={{
              width: '100%',
              padding: '14px 24px',
              background: cta.disabled 
                ? 'rgba(255,255,255,0.1)' 
                : 'linear-gradient(135deg, #4ade80, #22c55e)',
              border: 'none',
              borderRadius: '12px',
              color: cta.disabled ? 'rgba(255,255,255,0.4)' : 'white',
              fontSize: '16px',
              fontWeight: '600',
              cursor: cta.disabled ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {isWatching ? (
              <>
                <span className="spinner" style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTopColor: 'white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Reklam yükleniyor...
              </>
            ) : (
              <>
                🎬 Reklam İzle ve 1 Kredi Kazan
              </>
            )}
          </button>

          {/* Daily progress */}
          <div style={{ 
            marginTop: '12px', 
            display: 'flex', 
            justifyContent: 'center',
            gap: '4px'
          }}>
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                style={{
                  width: '24px',
                  height: '6px',
                  borderRadius: '3px',
                  background: i < (5 - rewardStatus.rewards_remaining) 
                    ? 'rgba(255,255,255,0.2)' 
                    : '#4ade80'
                }}
              />
            ))}
          </div>
          <p style={{ 
            color: 'rgba(255,255,255,0.4)', 
            fontSize: '11px',
            margin: '6px 0 0'
          }}>
            Bugün {5 - rewardStatus.rewards_remaining}/5 reklam izlediniz
          </p>
        </motion.div>

        {/* Result toast */}
        <AnimatePresence>
          {showResult && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              style={{
                position: 'fixed',
                bottom: '100px',
                left: '50%',
                transform: 'translateX(-50%)',
                padding: '16px 24px',
                background: showResult.type === 'success' 
                  ? 'rgba(74,222,128,0.95)' 
                  : 'rgba(239,68,68,0.95)',
                borderRadius: '12px',
                color: 'white',
                fontWeight: '500',
                zIndex: 9999,
                textAlign: 'center',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
              }}
            >
              {showResult.type === 'success' ? '🎉' : '❌'} {showResult.message}
            </motion.div>
          )}
        </AnimatePresence>

        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </>
    );
  }

  // Default variant
  return (
    <>
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        onClick={handleWatchAd}
        disabled={cta.disabled || isWatching}
        className={`watch-ad-btn ${className}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '14px 16px',
          background: cta.disabled 
            ? 'rgba(255,255,255,0.05)' 
            : 'rgba(74,222,128,0.1)',
          border: `1px solid ${cta.disabled ? 'rgba(255,255,255,0.1)' : 'rgba(74,222,128,0.3)'}`,
          borderRadius: '12px',
          cursor: cta.disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s',
          marginTop: '12px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '24px' }}>📺</span>
          <div style={{ textAlign: 'left' }}>
            <div style={{ 
              color: cta.disabled ? 'rgba(255,255,255,0.4)' : '#4ade80',
              fontWeight: '600',
              fontSize: '14px'
            }}>
              {isWatching ? 'Yükleniyor...' : cta.text}
            </div>
            <div style={{ 
              color: 'rgba(255,255,255,0.5)',
              fontSize: '12px'
            }}>
              {cta.subtext}
            </div>
          </div>
        </div>

        {!cta.disabled && !isWatching && (
          <div style={{
            background: '#4ade80',
            color: 'white',
            padding: '6px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '600'
          }}>
            +1 💎
          </div>
        )}
      </motion.button>

      {/* Result toast */}
      <AnimatePresence>
        {showResult && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            style={{
              position: 'fixed',
              bottom: '100px',
              left: '50%',
              transform: 'translateX(-50%)',
              padding: '16px 24px',
              background: showResult.type === 'success' 
                ? 'rgba(74,222,128,0.95)' 
                : 'rgba(239,68,68,0.95)',
              borderRadius: '12px',
              color: 'white',
              fontWeight: '500',
              zIndex: 9999,
              textAlign: 'center',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
            }}
          >
            {showResult.type === 'success' ? '🎉' : '❌'} {showResult.message}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default WatchAdButton;
